import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Linkedin, Send, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import Layout from "@/components/layout/Layout";
import PageHeader from "@/components/layout/PageHeader";
import Seo from "@/components/Seo";
import { CONTACT_INFO } from "@/data/siteData";
import { sendContactEmail } from "@/lib/emailjsClient";
import { trackFormSubmit } from "@/lib/gtm";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setIsSubmitting(true);
    try {
      await sendContactEmail(formData);
      trackFormSubmit("contact");
      setConsent(false);
      toast.success("Благодарим ви за съобщението!", {
        description: "Ще се свържем с вас възможно най-скоро.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Възникна грешка при изпращането.", {
        description: "Моля, опитайте отново или ни пишете директно на " + CONTACT_INFO.email,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout showContactStrip={false}>
      <Seo path="/kontakti" />
      <PageHeader 
        subtitle="Свържете се с нас"
        title="Контакти"
        description="Имате въпроси? Ще се радваме да ви помогнем"
      />

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="bg-slate-50 rounded-2xl p-8 mb-8">
                <h2 className="text-xl font-bold text-[#2E4A91] mb-6">
                  Информация за контакт
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2E4A91]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#2E4A91]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Имейл</p>
                      <a
                        href={`mailto:${CONTACT_INFO.email}`}
                        className="text-[#2E4A91] hover:underline"
                      >
                        {CONTACT_INFO.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2ED573]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#2ED573]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Телефон</p>
                      <a
                        href={`tel:${CONTACT_INFO.phone}`}
                        className="text-[#2E4A91] hover:underline"
                      >
                        {CONTACT_INFO.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2E4A91]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#2E4A91]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Адрес</p>
                      <p className="text-slate-600">{CONTACT_INFO.address}</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className="font-medium text-slate-900 mb-4">
                    Социални мрежи
                  </p>
                  <div className="flex gap-4">
                    <a
                      href={CONTACT_INFO.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-xl bg-[#1877F2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                      data-testid="social-facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href={CONTACT_INFO.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Outsource Point в LinkedIn"
                      className="w-12 h-12 rounded-xl bg-[#0A66C2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                      data-testid="social-linkedin"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-slate-200 h-64 lg:h-80">
                <iframe
                  title="Outsource Point Location"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent("Antim I 1, 3000 Vratsa, Bulgaria")}&z=16&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-slate-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#2E4A91] mb-6">
                Пишете ни
              </h2>
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Име
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="Вашето име"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    Имейл
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="email@example.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Телефон
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="+359 ..."
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="message"
                    className="text-slate-700 font-medium"
                  >
                    Съобщение
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="mt-2 bg-white min-h-[150px]"
                    placeholder="Вашето съобщение..."
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <ConsentCheckbox id="contact-consent" checked={consent} onChange={setConsent} />
                <Button
                  type="submit"
                  className="btn-primary w-full text-lg py-6"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Изпращане...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 w-5 h-5" />
                      Изпратете съобщение
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-500 mb-4">или</p>
                <Link to="/zapazi-chas">
                  <Button
                    className="btn-secondary w-full"
                    data-testid="contact-appointment-btn"
                  >
                    Запазете час за консултация
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
